const API_BASE = import.meta.env.VITE_API_BASE ?? '/api/v1';

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly body: string,
    message?: string,
  ) {
    super(message ?? `API ${status}: ${body}`);
    this.name = 'ApiError';
  }
}

/**
 * Mensagem legivel de um erro da API: o `message` do corpo quando e JSON do
 * Nest (string ou lista de validacao); senao o corpo cru. Sem isto a tela
 * mostrava `{"statusCode":500,"message":...}` para quem so queria saber
 * o que corrigir.
 */
export function mensagemDeErro(err: unknown): string {
  if (err instanceof ApiError) {
    try {
      const p = JSON.parse(err.body) as { message?: string | string[] };
      const m = Array.isArray(p.message) ? p.message.join('; ') : p.message;
      if (m) return m;
    } catch {
      /* corpo nao e JSON */
    }
    return err.body;
  }
  return err instanceof Error ? err.message : 'erro';
}

export interface ApiTokens {
  accessToken: string;
  refreshToken: string;
}

type TokenProvider = () => ApiTokens | null;
type RefreshFn = () => Promise<ApiTokens | null>;

let tokenProvider: TokenProvider = () => null;
let refreshFn: RefreshFn = async () => null;

export function configureApi(opts: { getTokens: TokenProvider; refresh: RefreshFn }) {
  tokenProvider = opts.getTokens;
  refreshFn = opts.refresh;
}

interface FetchOptions extends RequestInit {
  skipAuth?: boolean;
  /** Quando true, suprime o retry com refresh em 401 (uso interno) */
  _retried?: boolean;
}

export async function apiFetch<T>(path: string, init: FetchOptions = {}): Promise<T> {
  const headers = new Headers(init.headers);
  // FormData precisa que o browser configure Content-Type (com boundary) automaticamente.
  // Para JSON, default para application/json quando há corpo e nada foi definido.
  const isFormData = typeof FormData !== 'undefined' && init.body instanceof FormData;
  if (!headers.has('Content-Type') && init.body && !isFormData) {
    headers.set('Content-Type', 'application/json');
  }

  if (!init.skipAuth) {
    const tokens = tokenProvider();
    if (tokens) headers.set('Authorization', `Bearer ${tokens.accessToken}`);
  }

  const res = await fetch(`${API_BASE}${path}`, { ...init, headers });

  if (res.status === 401 && !init.skipAuth && !init._retried) {
    const fresh = await refreshFn();
    if (fresh) {
      return apiFetch<T>(path, { ...init, _retried: true });
    }
  }

  if (!res.ok) {
    const body = await res.text();
    throw new ApiError(res.status, body);
  }

  if (res.status === 204) return undefined as T;
  return res.json() as Promise<T>;
}

/**
 * Variante para baixar binarios (PDF, CSV, etc.) com auth.
 * Retorna o Blob; ideal pra abrir via URL.createObjectURL().
 */
export async function apiFetchBlob(
  path: string,
  init: FetchOptions = {},
): Promise<Blob> {
  const headers = new Headers(init.headers);
  if (!init.skipAuth) {
    const tokens = tokenProvider();
    if (tokens) headers.set('Authorization', `Bearer ${tokens.accessToken}`);
  }
  const res = await fetch(`${API_BASE}${path}`, { ...init, headers });
  if (res.status === 401 && !init.skipAuth && !init._retried) {
    const fresh = await refreshFn();
    if (fresh) {
      return apiFetchBlob(path, { ...init, _retried: true });
    }
  }
  if (!res.ok) {
    const body = await res.text();
    throw new ApiError(res.status, body);
  }
  return res.blob();
}
